import { useEffect, useState } from "react";
import { getMediaForDownload, getMediaObjectUrl } from "../../api/whatsapp.js";
import { useToast } from "../../components/ToastProvider.tsx";
import { openLightbox } from "../../lib/lightbox.js";
import type { WhatsappMessage } from "../../types.ts";
import { effectiveMessageKind } from "../Chat.helpers.ts";
import { SvgIcon } from "../chat-list/ChatAvatar.tsx";
import { icons } from "./icons.ts";
import { formatDuration } from "./recording.ts";

function useMediaUrl(messageId: unknown) {
  const [state, setState] = useState<{ url: string | null; error: string | null; loading: boolean }>({
    url: null,
    error: null,
    loading: !!messageId,
  });

  useEffect(() => {
    let active = true;
    if (!messageId) {
      setState({ url: null, error: "missing message id", loading: false });
      return () => { active = false; };
    }
    setState({ url: null, error: null, loading: true });
    getMediaObjectUrl(messageId).then((url: string) => {
      if (active) setState({ url, error: null, loading: false });
    }).catch((err: unknown) => {
      if (active) setState({ url: null, error: err instanceof Error ? err.message : "Falha", loading: false });
    });
    return () => { active = false; };
  }, [messageId]);

  return state;
}

export function ImageBubble({ message }: { message: WhatsappMessage }) {
  const media = useMediaUrl(message.id);
  return (
    <div className="wa-media-image">
      {media.loading ? <div className="wa-media-loading">Carregando imagem...</div> : null}
      {media.error ? <div className="wa-media-loading wa-media-error">Falha ao carregar imagem</div> : null}
      {media.url ? (
        <img
          src={media.url}
          alt="Imagem"
          className="wa-media-img"
          loading="lazy"
          onClick={() => openLightbox({
            src: media.url || "",
            kind: "image",
            caption: message.text || "",
            messageId: message.id,
          })}
        />
      ) : null}
      {message.text ? <div className="wa-media-caption">{message.text}</div> : null}
    </div>
  );
}

export function VideoBubble({ message }: { message: WhatsappMessage }) {
  const media = useMediaUrl(message.id);
  return (
    <div className="wa-media-video">
      {media.loading ? <div className="wa-media-loading">{message.kind === "gif" ? "Carregando GIF..." : "Carregando video..."}</div> : null}
      {media.error ? <div className="wa-media-loading wa-media-error">Falha ao carregar video</div> : null}
      {media.url ? (
        <video
          src={media.url}
          className="wa-media-vid"
          controls
          preload="metadata"
          onClick={() => openLightbox({
            src: media.url || "",
            kind: "video",
            caption: message.text || "",
            messageId: message.id,
          })}
        />
      ) : null}
      {message.text ? <div className="wa-media-caption">{message.text}</div> : null}
    </div>
  );
}

export function AudioBubble({ message }: { message: WhatsappMessage }) {
  const media = useMediaUrl(message.id);
  const isVoice = effectiveMessageKind(message) === "voice";
  const label = isVoice ? "Mensagem de voz" : "Audio";
  const [duration, setDuration] = useState<number | null>(null);
  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!audioRef) return;
    const onLoad = () => {
      const d = audioRef.duration;
      if (Number.isFinite(d) && d > 0) {
        setDuration(d);
      } else if (d === Infinity) {
        audioRef.currentTime = 1e10;
        const onUpdate = () => {
          if (Number.isFinite(audioRef.duration)) {
            setDuration(audioRef.duration);
            audioRef.currentTime = 0;
          }
          audioRef.removeEventListener("timeupdate", onUpdate);
        };
        audioRef.addEventListener("timeupdate", onUpdate);
      }
    };
    audioRef.addEventListener("loadedmetadata", onLoad);
    if (audioRef.readyState >= 1) onLoad();
    return () => audioRef.removeEventListener("loadedmetadata", onLoad);
  }, [audioRef, media.url]);

  return (
    <div className={`wa-media-audio ${isVoice ? "is-voice" : ""}`}>
      <div className="wa-audio-head">
        <span className="wa-audio-icon" aria-hidden="true">
          <SvgIcon svg={isVoice ? icons.mic : icons.audio} />
        </span>
        <span className="wa-audio-meta">
          <span className="wa-audio-label">{label}</span>
          {duration != null ? (
            <span className="wa-audio-dur">{formatDuration(duration)}</span>
          ) : null}
        </span>
      </div>
      {media.loading ? <div className="wa-media-loading">Carregando...</div> : null}
      {media.error ? <div className="wa-media-loading wa-media-error">Falha ao carregar audio</div> : null}
      {media.url ? (
        <audio
          ref={setAudioRef}
          src={media.url}
          controls
          preload="metadata"
          className="wa-media-aud"
        />
      ) : null}
    </div>
  );
}

export function StickerBubble({ message }: { message: WhatsappMessage }) {
  const media = useMediaUrl(message.id);
  return (
    <div className="wa-media-sticker">
      {media.loading ? <div className="wa-media-loading">Carregando...</div> : null}
      {media.error ? <div className="wa-media-loading wa-media-error">Figurinha indisponivel</div> : null}
      {media.url ? <img src={media.url} alt="Sticker" className="wa-media-stk" /> : null}
    </div>
  );
}

export function DocumentBubble({ message }: { message: WhatsappMessage }) {
  const { showToast } = useToast();
  const [downloading, setDownloading] = useState(false);
  const rawName = String(message.mediaFileName || message.mediaFilename || "").trim();
  const mime = String(message.mediaMime || "");
  const size = typeof message.mediaSize === "number" ? message.mediaSize : 0;
  const ext = extensionFor(rawName, mime);
  const color = extensionColor(ext);
  const sizeLabel = formatBytes(size);
  const looksLikeInternalId = !!rawName && /^[A-F0-9]{15,}\.?[a-zA-Z0-9]{0,5}$/.test(rawName);
  const cleanRaw = looksLikeInternalId ? "" : rawName;
  const filename = cleanRaw || "Documento";
  const downloadName = (() => {
    if (cleanRaw && /\.[A-Za-z0-9]{1,5}$/.test(cleanRaw)) return cleanRaw;
    const extLow = ext.toLowerCase();
    const kindLabel: Record<string, string> = {
      document: "documento", image: "imagem", video: "video",
      audio: "audio", voice: "audio", sticker: "sticker", gif: "imagem",
    };
    const prefix = kindLabel[String(message.kind || "")] || "arquivo";
    return `${prefix}-${message.id || Date.now()}.${extLow === "file" ? "bin" : extLow}`;
  })();

  async function download() {
    if (downloading) return;
    setDownloading(true);
    try {
      const { url, filename: serverFilename } = await getMediaForDownload(message.id, downloadName);
      const a = document.createElement("a");
      a.href = url;
      a.download = serverFilename || downloadName;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Falha ao baixar", "error");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <button
      type="button"
      className="wa-media-doc"
      onClick={() => void download()}
      title={`Baixar ${filename}`}
      disabled={downloading}
    >
      <span className="wa-doc-thumb" style={{ background: color }} aria-hidden="true">
        <span className="wa-doc-ext">{ext}</span>
      </span>
      <span className="wa-doc-meta">
        <span className="wa-doc-name">{filename}</span>
        <span className="wa-doc-sub">
          {sizeLabel ? <span>{sizeLabel}</span> : null}
          {sizeLabel && ext ? <span className="wa-doc-sep">Â·</span> : null}
          <span>{ext}</span>
        </span>
      </span>
      <span className="wa-doc-dl" aria-hidden="true">
        {downloading ? (
          <span className="wa-doc-spin" />
        ) : (
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        )}
      </span>
    </button>
  );
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function extensionFor(filename: string, mime: string): string {
  const m = filename.match(/\.([A-Za-z0-9]{1,5})$/);
  if (m) return m[1].toUpperCase();
  const map: Record<string, string> = {
    "application/pdf": "PDF",
    "application/msword": "DOC",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
    "application/vnd.ms-excel": "XLS",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
    "application/vnd.ms-powerpoint": "PPT",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PPTX",
    "text/csv": "CSV",
    "text/plain": "TXT",
    "application/zip": "ZIP",
    "application/x-rar-compressed": "RAR",
    "image/jpeg": "JPG",
    "image/png": "PNG",
  };
  return map[String(mime).toLowerCase().split(";")[0].trim()] || "FILE";
}

function extensionColor(ext: string): string {
  const colors: Record<string, string> = {
    PDF: "#d8483a",
    DOC: "#2a5599", DOCX: "#2a5599",
    XLS: "#1f7244", XLSX: "#1f7244", CSV: "#1f7244",
    PPT: "#c43e1c", PPTX: "#c43e1c",
    ZIP: "#7a5a2e", RAR: "#7a5a2e",
    TXT: "#5a6268",
    JPG: "#8e44ad", PNG: "#8e44ad",
  };
  return colors[ext] || "#5157ae";
}
